"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Song } from "../types/types"; // Adjust path to your types file
import React from "react";
import exp from "constants";

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  stripeProductID: string;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (song: Song) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = async (song: Song): Promise<void> => {
    try {
      const response = await fetch(
        `/api/get-price?stripeProductID=${encodeURIComponent(
          song.stripeProductID
        )}`
      );
      if (!response.ok) throw new Error("Failed to fetch price");
      const { price } = await response.json();
      if (!price) throw new Error("No price available");

      setCart((prev) => {
        const existingItem = prev.find((item) => item.id === song.id);
        if (existingItem) {
          return prev.map((item) =>
            item.id === song.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        }
        return [
          ...prev,
          {
            id: song.id,
            name: song.title,
            price,
            quantity: 1,
            stripeProductID: song.stripeProductID,
          },
        ];
      });
    } catch (error) {
      console.error("Add to cart error:", error);
      alert("Could not add item to cart due to a price fetch error.");
    }
  };

  const updateQuantity = (id: string, quantity: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
  };

  const contextValue: CartContextType = {
    cart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
  };

  return React.createElement(
    CartContext.Provider,
    { value: contextValue },
    children
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
export default CartProvider;
